import { css } from '@linaria/core';
import { Button, Result, Typography } from 'antd';

import { FallbackProps } from 'react-error-boundary';

import ContentLoading from '@components/content-loading';
import AppError from '@lib/app-error';

import { reloadPage } from './navigation';
import useAsyncResourceRecovery from './use-async-resource-recovery';

const styles = {
  reference: css`
    margin-top: var(--ant-margin-sm);
  `,
  result: css`
    margin-block: auto;
  `,
};

interface ErrorPresentation {
  description: string;
  status: '403' | '404' | '500' | 'warning';
  title: string;
}

const DEFAULT_ERROR_DESCRIPTION =
  'Tente novamente. Se o problema continuar, entre em contato com o suporte.';

const getErrorPresentation = (error: unknown): ErrorPresentation => {
  const appError = error instanceof AppError ? error : null;
  const description = appError?.message || DEFAULT_ERROR_DESCRIPTION;

  if (appError?.isServiceUnavailable) {
    return {
      description: 'Nossos servidores não estão respondendo no momento. Por favor, tente novamente em instantes.',
      status: 'warning',
      title: 'Serviço temporariamente indisponível',
    };
  }

  if (appError?.isAccountVerificationRequired) {
    return {
      description:
        'Atualize ou verifique sua conta na página de perfil para continuar.',
      status: 'warning',
      title: 'Sua conta precisa ser verificada.',
    };
  }

  if (appError?.isNotFound) {
    return {
      description: 'O recurso solicitador não foi encontrado ou foi removido.',
      status: '404',
      title: 'Recurso não encontrado',
    };
  }

  if (appError?.isForbidden) {
    return {
      description,
      status: '403',
      title: 'Você não tem acesso a este conteúdo.',
    };
  }

  return {
    description,
    status: '500',
    title: 'Não foi possível carregar os dados.',
  };
};

const InternalErrorBoundary = ({ error, resetErrorBoundary }: FallbackProps) => {
  const recovering = useAsyncResourceRecovery(error);

  const presentation = getErrorPresentation(error);
  const appError = error instanceof AppError ? error : null;
  const errorId = appError?.errorId ?? appError?.requestId;
  const errorCode = appError?.code;

  const onRetry = () => {
    reloadPage();
    resetErrorBoundary();
  };

  const handleGoHome = () => {
    resetErrorBoundary();
    window.location.href = '/';
  };

  if (recovering) {
    return <ContentLoading />;
  }

  return (
    <Result
      className={styles.result}
      status={presentation.status}
      title={presentation.title}
      subTitle={presentation.description}
      extra={[
        <Button key="retry" type="primary" onClick={onRetry}>
          Tentar novamente
        </Button>,
        <Button key="home" onClick={handleGoHome}>
          Página inicial
        </Button>,
      ]}
    >
      {errorId ? (
        <div className={styles.reference}>
          <Typography.Text type="secondary">
            Código do erro:{' '}
          </Typography.Text>
          <Typography.Text strong copyable>
            {errorId}
          </Typography.Text>
        </div>
      ) : errorCode && errorCode !== 'unknown' ? (
        <div className={styles.reference}>
          <Typography.Text type="secondary" copyable>
            Referência: {errorCode}
          </Typography.Text>
        </div>
      ) : null}
    </Result>
  );
};

export default InternalErrorBoundary;
